import { Award, CheckCircle, FileText } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CERTIFICATIONS = [
  {
    name: "ISO 9001:2015",
    issuer: "Quality Management Systems",
    desc: "International standard for quality management systems ensuring consistent product quality and customer satisfaction.",
    year: "Valid through 2027",
  },
  {
    name: "ISO 22000",
    issuer: "Food Safety Management",
    desc: "Globally recognized food safety management standard covering the entire food chain from farm to table.",
    year: "Valid through 2027",
  },
  {
    name: "HALAL Certification",
    issuer: "Islamic Food & Nutrition Council",
    desc: "Certified HALAL processing and handling procedures for Muslim-majority markets worldwide.",
    year: "Valid through 2026",
  },
  {
    name: "Organic Certification",
    issuer: "Control Union (USDA/EU Organic)",
    desc: "Select products available with USDA and EU organic certification. Premium pricing applies.",
    year: "On request",
  },
  {
    name: "Phytosanitary Certificate",
    issuer: "Pakistan Plant Protection Dept.",
    desc: "Required export documentation certifying products are free from plant pests and diseases.",
    year: "Per shipment",
  },
  {
    name: "SGS/Bureau Veritas COA",
    issuer: "Third-party Lab Analysis",
    desc: "Independent Certificate of Analysis available for all shipments. Covers purity, moisture, contaminants.",
    year: "On request",
  },
];

const SAMPLE_COA = [
  { param: "Purity", spec: "\u2265 98%", result: "98.7%", status: "Pass" },
  { param: "Moisture", spec: "12-14%", result: "13.2%", status: "Pass" },
  { param: "Foreign Matter", spec: "< 1%", result: "0.4%", status: "Pass" },
  { param: "Broken Grains", spec: "< 3%", result: "1.8%", status: "Pass" },
  { param: "Aflatoxin (ppb)", spec: "< 20", result: "< 5", status: "Pass" },
  { param: "Heavy Metals", spec: "Compliant", result: "Compliant", status: "Pass" },
];

export default function Certifications() {
  return (
    <div data-testid="page-certifications" className="py-16 md:py-20">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="overline">Compliance · Quality · Trust</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight max-w-4xl">
          Certified to international standards.
        </h1>
        <p className="mt-8 text-foreground/75 text-lg max-w-3xl leading-relaxed">
          We maintain all necessary certifications for export to major markets worldwide. 
          Full documentation and third-party verification available on every shipment.
        </p>

        {/* Certifications Grid */}
        <div className="mt-20">
          <div className="overline mb-10">Active Certifications</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {CERTIFICATIONS.map((cert, i) => (
              <div key={i} className="bg-[#0A0909] p-8 hover:bg-[#141212] transition-colors">
                <Award size={28} className="text-[#3FD97A]" />
                <h3 className="mt-6 font-serif text-2xl">{cert.name}</h3>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {cert.issuer}
                </p>
                <p className="mt-4 text-sm text-foreground/70 leading-relaxed">{cert.desc}</p>
                <div className="mt-4 flex items-center gap-2">
                  <CheckCircle size={14} className="text-[#3FD97A]" />
                  <span className="font-mono text-[11px] text-[#3FD97A]">{cert.year}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sample COA */}
        <div className="mt-28">
          <div className="overline mb-10">Sample Certificate of Analysis (COA)</div>
          <div className="border border-border">
            <div className="p-6 bg-[#0A0909] border-b border-border">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-serif text-3xl">Super Basmati Rice — Batch #RC-2026-1234</h2>
                  <p className="mt-2 text-sm text-foreground/70">Lab: SGS Pakistan · Date: March 2026 · Sampling: Random composite</p>
                </div>
                <FileText size={32} className="text-[#3FD97A]" />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Parameter</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Specification</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Test Result</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {SAMPLE_COA.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-sm font-semibold">{row.param}</TableCell>
                    <TableCell>{row.spec}</TableCell>
                    <TableCell className="font-semibold">{row.result}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2 text-[#3FD97A]">
                        <CheckCircle size={14} /> {row.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-6 text-sm text-foreground/60 italic">
            * This is a sample COA format. Actual certificates are provided per shipment with official lab letterhead and signatures.
          </p>
        </div>

        {/* Compliance Note */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
          <div className="bg-[#0A0909] p-10">
            <h3 className="font-serif text-3xl">Export Documentation</h3>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              Every shipment includes: Commercial Invoice, Packing List, Phytosanitary Certificate, 
              Certificate of Origin, Bill of Lading, and optional third-party COA.
            </p>
          </div>
          <div className="bg-[#0A0909] p-10">
            <h3 className="font-serif text-3xl">Customs Compliance</h3>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              We handle all Pakistan customs formalities. For destination customs, we provide complete 
              documentation and support your clearing agent with any technical queries.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
